<script setup lang="ts">
// /repair-or-replace — mockup page 3 (M4).
//
// Seven questions laid out as columns so the whole assessment is visible at
// once, then a verdict. The score comes from the shared contract via
// scorer.ts for instant display, and the completed walk rides along to the
// Second Opinion form as context — so the pro receives the same numbers the
// visitor saw, recomputed server-side rather than trusted from the browser.
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { NInput } from 'naive-ui'
import treeJson from '@contracts/decision-tree.askhvac-repair-replace.v1.json'
import { leadIntent } from '@/shared/state/leadIntent'
import { scoreAssessment } from '../scorer'
import { contactContext, contactContextLabel } from '../store'

interface Opt {
    value: string
    label: string
}
interface Node {
    prompt: string
    options?: Opt[]
}

const tree = treeJson as unknown as { id: string; nodes: Record<string, Node> }

const ORDER = ['age_band', 'problem', 'quote_band', 'system_size', 'comfort', 'staying'] as const

const answers = ref<Record<string, string | null>>({
    age_band: null,
    problem: null,
    quote_band: null,
    system_size: null,
    comfort: null,
    staying: null,
})
const zip = ref('')
const showResult = ref(false)

const answeredCount = computed(() => Object.values(answers.value).filter(Boolean).length)
const step = computed(() => Math.min(answeredCount.value + 1, 7))
const complete = computed(() => answeredCount.value === ORDER.length)

const verdict = computed(() => scoreAssessment(answers.value))

function pick(nodeId: string, value: string) {
    answers.value = { ...answers.value, [nodeId]: value }
}

function seeRecommendation() {
    if (!complete.value) return
    showResult.value = true

    // Carry the walk to the lead form. The backend replays these answers
    // against the same contract, so the relayed verdict is computed, not
    // reported by the browser.
    const walk: { node_id: string; value: unknown }[] = ORDER.map((id) => ({
        node_id: id,
        value: answers.value[id],
    }))
    // The server replays the walk and requires it to reach the terminal, so
    // the optional ZIP node still needs an explicit answer — null when the
    // visitor skipped it. Omitting the node entirely fails validation and
    // the lead is rejected at submit time.
    const typedZip = zip.value.trim()
    walk.push({ node_id: 'zip', value: /^\d{5}$/.test(typedZip) ? typedZip : null })
    contactContext.value = { tree_id: tree.id, answers: walk }
    contactContextLabel.value = 'Repair or Replace assessment'

    // A REPLACE verdict is evidence, not a visitor statement — leave the
    // intent unstated so the backend records it as derived. An explicit
    // REPAIR lean is likewise not a claim the visitor made.
    leadIntent.value = null

    requestAnimationFrame(() =>
        document.getElementById('result')?.scrollIntoView({ behavior: 'smooth' }),
    )
}

const AGE_FACTS = [
    {
        title: '10–15 Years Average Lifespan',
        body: 'Most AC systems in Florida last 10–15 years — heat and humidity shorten the run.',
        icon: '<path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm.8 8.5-3.3 2-.8-1.3 2.5-1.5V5.2h1.6z"/>',
    },
    {
        title: 'Rising Repair Costs',
        body: 'Repairs get more frequent — and more expensive — as a system ages.',
        icon: '<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>',
    },
    {
        title: 'Lower Efficiency',
        body: 'Older systems use more energy and cost more every month.',
        icon: '<path d="M11 2 4 12h5l-1 6 7-10h-5z"/>',
    },
    {
        title: 'Less Comfort',
        body: 'Harder to keep even temperatures and to pull humidity out of the house.',
        icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>',
    },
]
</script>

<template>
    <section class="hero">
        <div class="wash" aria-hidden="true"></div>
        <div class="inner">
            <div>
                <p class="crumb">Home <span>›</span> Repair or Replace</p>
                <h1>
                    Should You <span class="hl">Repair</span><br />
                    or <span class="hl">Replace</span> Your AC?
                </h1>
                <p class="lede">
                    Answer a few questions about your system and we'll give you a recommendation
                    built from the same rules technicians use — with every factor shown.
                </p>
                <ul class="chips">
                    <li>⏱ Takes 2–3 minutes</li>
                    <li>🛡 100% free, no obligation</li>
                    <li>🔒 Private &amp; secure</li>
                    <li>⚖ Independent guidance</li>
                </ul>
            </div>

            <aside class="why">
                <h2>Why It Matters</h2>
                <p>
                    In Florida, most AC systems last 10–15 years. As systems age, repair costs rise
                    and efficiency drops — costing you every month.
                </p>
            </aside>
        </div>
    </section>

    <section class="assess">
        <div class="assess-inner">
            <header class="assess-head">
                <div>
                    <h2>Your AC Repair or Replace Assessment</h2>
                    <p>Tell us about your system so we can give you the best recommendation.</p>
                </div>
                <p class="step">
                    Step {{ step }} of 7
                    <span class="dots" aria-hidden="true">
                        <i v-for="n in 7" :key="n" :class="{ on: n <= answeredCount }"></i>
                    </span>
                </p>
            </header>

            <div class="columns">
                <div v-for="(nodeId, i) in ORDER" :key="nodeId" class="column">
                    <span class="n">{{ i + 1 }}</span>
                    <h3>{{ tree.nodes[nodeId].prompt }}</h3>
                    <button
                        v-for="opt in tree.nodes[nodeId].options"
                        :key="opt.value"
                        type="button"
                        class="opt"
                        :class="{ picked: answers[nodeId] === opt.value }"
                        @click="pick(nodeId, opt.value)"
                    >
                        {{ opt.label }}
                    </button>
                </div>

                <div class="column">
                    <span class="n">7</span>
                    <h3>ZIP code to localize results</h3>
                    <NInput v-model:value="zip" placeholder="Enter ZIP" :maxlength="5" inputmode="numeric" />
                    <button class="see" type="button" :disabled="!complete" @click="seeRecommendation">
                        See My Recommendation →
                    </button>
                    <p v-if="!complete" class="hint">Answer all six questions to continue.</p>
                </div>
            </div>

            <p class="privacy">🔒 We never sell your information. It is used only to produce your assessment.</p>
        </div>
    </section>

    <section v-if="showResult" id="result" class="result">
        <div class="result-inner">
            <h2>Your Recommendation</h2>

            <div class="verdicts">
                <div class="verdict repair" :class="{ dim: verdict.band === 'REPLACE' }">
                    <header><strong>Repair</strong><span class="pct">{{ verdict.repairPct }}%</span></header>
                    <p>Repairing your system could be a shorter-term solution.</p>
                    <ul>
                        <li>Lower upfront cost</li>
                        <li>Good option for newer systems</li>
                        <li>May solve the problem temporarily</li>
                    </ul>
                </div>

                <span class="or">OR</span>

                <div class="verdict replace" :class="{ dim: verdict.band === 'REPAIR' }">
                    <header><strong>Replace</strong><span class="pct">{{ verdict.replacePct }}%</span></header>
                    <p>Replacing your system is likely the smarter long-term investment.</p>
                    <ul>
                        <li>Better comfort &amp; humidity control</li>
                        <li>Lower energy bills</li>
                        <li>Avoids stacking future repair costs</li>
                        <li>New system warranty protection</li>
                    </ul>
                </div>

                <aside class="means">
                    <h3>What This Means</h3>
                    <p class="band">
                        Verdict: <strong>{{ verdict.band }}</strong> ({{ verdict.score }}/100)
                    </p>
                    <ul class="factors">
                        <li v-for="f in verdict.factors" :key="f">{{ f }}</li>
                        <li v-if="!verdict.factors.length">
                            Nothing in your answers pushes toward replacement.
                        </li>
                    </ul>
                    <p class="fineprint">
                        Every factor above comes from your answers — no hidden weighting, and no
                        sales pitch. This is guidance, not a diagnosis; only an on-site technician
                        can confirm what's wrong.
                    </p>
                    <RouterLink class="cta" to="/second-opinion">Get a Free Second Opinion</RouterLink>
                    <p class="hint">Your answers ride along, so the pro starts informed.</p>
                </aside>
            </div>
        </div>
    </section>

    <section class="age">
        <div class="age-inner">
            <h2>Why Age Makes a Big Difference</h2>
            <div class="age-grid">
                <div v-for="fact in AGE_FACTS" :key="fact.title" class="fact">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="fact.icon"></svg>
                    </span>
                    <strong>{{ fact.title }}</strong>
                    <p>{{ fact.body }}</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    position: relative;
    background: var(--site-gradient-navy);
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: radial-gradient(80% 80% at 85% 10%, rgba(29, 95, 191, 0.5) 0%, rgba(7, 28, 69, 0) 65%);
}

.inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 17rem;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.6);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3.4vw, 2.6rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
}

.lede {
    max-width: 34rem;
    margin: 0 0 var(--site-space-lg);
    color: rgba(255, 255, 255, 0.85);
}

.chips {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    font-size: 0.8rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
}

.why {
    background: rgba(7, 28, 69, 0.75);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        margin: 0 0 0.4rem;
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.8);
    }
}

/* ---- assessment ---- */
.assess {
    background: var(--site-bg-alt);
}

.assess-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.assess-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    margin-bottom: var(--site-space-lg);

    h2 {
        font-family: var(--site-font-heading);
        font-size: clamp(1.3rem, 2.4vw, 1.7rem);
        font-weight: 800;
        color: var(--site-navy);
        margin: 0;
    }

    p {
        margin: 0.2rem 0 0;
        font-size: 0.85rem;
        color: var(--site-text-muted);
    }
}

.step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--site-navy);

    .dots {
        display: inline-flex;
        gap: 4px;
    }

    i {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--site-border);

        &.on {
            background: var(--site-accent);
        }
    }
}

.columns {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: var(--site-space-sm);
    background: #fff;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
}

.column {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0 0.3rem;

    h3 {
        font-size: 0.76rem;
        font-weight: 700;
        color: var(--site-navy);
        line-height: 1.3;
        margin: 0 0 0.2rem;
        min-height: 2.4em;
    }
}

.n {
    display: grid;
    place-items: center;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: 50%;
    background: var(--site-blue);
    color: #fff;
    font-size: 0.76rem;
    font-weight: 700;
}

.opt {
    padding: 0.42rem 0.5rem;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-sm);
    background: #fff;
    color: var(--site-text);
    font-family: var(--site-font-body);
    font-size: 0.76rem;
    text-align: left;
    cursor: pointer;

    &:hover {
        border-color: var(--site-blue);
    }

    &.picked {
        background: var(--site-blue);
        border-color: var(--site-blue);
        color: #fff;
        font-weight: 600;
    }

    /* These are the whole interaction on this page — seven questions, all
       answered by tapping one of these. At 30px they were the smallest
       targets on the site and the easiest to mis-hit, which on a
       single-select means silently answering the wrong thing. */
    @media (max-width: 860px) {
        min-height: 44px;
        padding: 0.6rem 0.75rem;
        font-size: 0.9rem;
    }
}

.see {
    margin-top: 0.4rem;
    padding: 0.55rem 0.5rem;
    border: none;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-family: var(--site-font-body);
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    /* The payoff button for the whole assessment. It was 33px — smaller
       than the options feeding it. */
    @media (max-width: 860px) {
        min-height: 48px;
        margin-top: 0.7rem;
        font-size: 0.95rem;
    }
}

.hint {
    margin: 0.3rem 0 0;
    font-size: 0.68rem;
    color: var(--site-text-muted);

    @media (max-width: 860px) {
        font-size: 0.78rem;
    }
}

.privacy {
    margin: var(--site-space-md) 0 0;
    font-size: 0.74rem;
    color: var(--site-text-muted);
}

/* ---- result ---- */
.result {
    background: var(--site-bg);
}

.result-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);

    h2 {
        font-family: var(--site-font-heading);
        font-size: clamp(1.3rem, 2.4vw, 1.8rem);
        font-weight: 800;
        color: var(--site-navy);
        margin: 0 0 var(--site-space-lg);
    }
}

.verdicts {
    display: grid;
    grid-template-columns: 1fr auto 1fr 1.05fr;
    gap: var(--site-space-md);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;

        .or {
            display: none;
        }
    }
}

.verdict {
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);
    border: 1px solid var(--site-border);
    transition: opacity 0.2s ease;

    header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        margin-bottom: 0.4rem;
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 1.05rem;
    }

    .pct {
        font-family: var(--site-font-heading);
        font-size: 1.5rem;
        font-weight: 800;
    }

    p {
        margin: 0 0 var(--site-space-sm);
        font-size: 0.83rem;
        color: var(--site-text-muted);
    }

    ul {
        margin: 0;
        padding-left: 1.1rem;
        font-size: 0.8rem;
        line-height: 1.6;
    }

    &.dim {
        opacity: 0.55;
    }

    &.repair {
        background: var(--site-green-soft);
        border-color: #bfe6cd;

        strong,
        .pct {
            color: #15803d;
        }
    }

    &.replace {
        background: var(--site-accent-soft);
        border-color: #f8cdb6;

        strong,
        .pct {
            color: var(--site-accent-strong);
        }
    }
}

.or {
    align-self: center;
    font-weight: 700;
    color: var(--site-text-muted);
}

.means {
    background: var(--site-bg-alt);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        color: var(--site-navy);
        margin: 0 0 0.3rem;
    }

    .band {
        margin: 0 0 var(--site-space-sm);
        font-size: 0.86rem;
    }
}

.factors {
    margin: 0 0 var(--site-space-sm);
    padding-left: 1.1rem;
    font-size: 0.78rem;
    line-height: 1.55;
    color: var(--site-text-muted);
}

.fineprint {
    margin: 0 0 var(--site-space-md);
    font-size: 0.72rem;
    line-height: 1.5;
    color: var(--site-text-muted);
}

.cta {
    display: block;
    text-align: center;
    padding: 0.6rem 1rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.88rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }
}

/* ---- age facts ---- */
.age {
    background: var(--site-bg-alt);
}

.age-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);

    h2 {
        font-family: var(--site-font-heading);
        font-size: clamp(1.3rem, 2.4vw, 1.7rem);
        font-weight: 800;
        color: var(--site-navy);
        margin: 0 0 var(--site-space-lg);
    }
}

.age-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--site-space-md);

    @media (max-width: 860px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.fact {
    background: #fff;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);
    text-align: center;

    strong {
        display: block;
        font-size: 0.9rem;
        color: var(--site-navy);
        margin-bottom: 0.25rem;
    }

    p {
        margin: 0;
        font-size: 0.78rem;
        line-height: 1.5;
        color: var(--site-text-muted);
    }
}

.chip {
    display: inline-grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    background: var(--site-blue-soft);
    color: var(--site-blue);
    margin-bottom: var(--site-space-sm);

    svg {
        width: 1.4rem;
        height: 1.4rem;
    }
}
</style>
